/* eslint-disable react/sort-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Slider from '@material-ui/core/Slider';
import Tooltip from '@material-ui/core/Tooltip';
import {
  Pause,
  PlayArrow,
  PictureInPictureAlt,
  VolumeDown,
  VolumeOff,
  VolumeUp,
} from '@material-ui/icons';

import { translate } from 'services/i18nService';
import SessionStorageService from 'services/SessionStorageService';
import { generateID } from 'utils/helpers';

import ControlsWrapper from './ControlsWrapper';
import FullScreen from './FullScreen';
import StreamQualityOptions from './StreamQualityOptions';
import TheatreMode from './TheathreMode';
import styles from './Controls.css';


const HALF_VOLUME = 50;

@observer
class ViewerControls extends Component {
  static propTypes = {
    // Player & controls service
    playerService: PropTypes.object.isRequired,
    isDesktop: PropTypes.bool,
    // Mute the video (volume 0)
    muted: PropTypes.bool,
    // Toggle video seeking controls
    showSeekBar: PropTypes.bool,
    // Toggle play/pause button
    showPlayPause: PropTypes.bool,
    // Toggle volume controls
    volumeControls: PropTypes.bool,
    showFullscreen: PropTypes.bool,
    // Show picture in picture option
    pip: PropTypes.bool,
    isJinkPlayer: PropTypes.bool.isRequired,
    volume: PropTypes.number,
    handleVolume: PropTypes.func,

    /* Callbacks */
    // Callback triggered when pressing play
    onPlay: PropTypes.func,
    // Callback triggered when pausing the video
    onPause: PropTypes.func,
    // Callback  triggered when entering fullscreen
    onFullScreen: PropTypes.func.isRequired,
    // Callback triggered when entering theatre mode
    onTheatreMode: PropTypes.func,
    // Called when volume is changed
    onVolumeChange: PropTypes.func,
    // Callback triggered when stream quality is changed
    onStreamQualityChange: PropTypes.func,

    /* Wrappers */
    videoAndChatWrapper: PropTypes.object,

    /* Jink */
    jinkPlayer: PropTypes.object.isRequired,
  };

  constructor(...args) {
    super(...args);

    this.IDs = {
      prefix: '4545hhiP',
      element: 'videoControls',
      muteTippingSounds: 'muteTippingSounds',
      unmuteTippingSounds: 'unmuteTippingSounds',
      volumeSlider: 'volumeSlider',
      muteStream: 'muteStream',
      unmuteStream: 'unmuteStream',
    };
    this.makeId = generateID(this.IDs.prefix, this.IDs.element);
  }

  sliderValue = () => {
    const { isJinkPlayer, jinkPlayer, playerService, volume } = this.props;
    if (isJinkPlayer) {
      return jinkPlayer.muted() ? 0 : volume * 100;
    }

    return playerService.isVideoMuted ? 0 : playerService.volume;

  };

  volumeValue = () => {
    const { isJinkPlayer, playerService, volume } = this.props;
    if (isJinkPlayer) {

      return volume * 100;
    }

    return playerService.volume;

  };

  isMuted = () => {
    const { isJinkPlayer, jinkPlayer, playerService } = this.props;
    if (isJinkPlayer) {

      return jinkPlayer.muted();
    }

    return playerService.isVideoMuted;

  };

  /**
   * Checks weather the callback function exists.
   * @param  {Function} prop  - Callback function
   */
  checkCallbacks = (prop) => prop && typeof prop === 'function';

  playOrPauseIcon() {
    const { isPlaying } = this.props.playerService;

    if (isPlaying) {
      return (<Pause onClick={this.onPause} />);
    }

    return (<PlayArrow onClick={this.onPlay} />);
  }

  getVolumeIcon() {
    const { videoAndChatWrapper } = this.props;
    const popperProps = { container: videoAndChatWrapper };
    const muteID = this.makeId(this.IDs.muteStream);
    const unmuteID = this.makeId(this.IDs.unmuteStream);

    if (this.volumeValue() < HALF_VOLUME && !this.isMuted()) {
      return (
        <Tooltip
          PopperProps={popperProps}
          placement="top"
          title={translate('v2profile.player.controls.mute.stream')}
        >
          <VolumeDown
            className={styles.volumeControlsItem}
            id={muteID}
            onClick={this.onMuteVideo}
          />
        </Tooltip>
      );
    }
    if (this.volumeValue() === 0 || this.isMuted()) {
      return (
        <Tooltip
          PopperProps={popperProps}
          placement="top"
          title={translate('v2profile.player.controls.unmute.stream')}
        >
          <VolumeOff
            className={styles.volumeControlsItem}
            id={unmuteID}
            onClick={this.onMuteVideo}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip
        PopperProps={popperProps}
        placement="top"
        title={translate('v2profile.player.controls.mute.stream')}
      >
        <VolumeUp
          className={styles.volumeControlsItem}
          id={muteID}
          onClick={this.onMuteVideo}
        />
      </Tooltip>
    );
  }

  leftComponent() {
    const {
      showPlayPause,
      volumeControls,
      playerService,
    } = this.props;

    const { timeDurationString } = playerService;

    const sliderValue = this.sliderValue();
    const volumeIcon = this.getVolumeIcon();
    const playOrPauseIcon = this.playOrPauseIcon();
    const sliderID = this.makeId(this.IDs.volumeSlider);

    const sliderClasses = [
      styles.volumeSlider,
      styles.volumeControlsItem,
    ];

    return (
      <>
        {showPlayPause && (
          <>
            {playOrPauseIcon}
            <span>{timeDurationString}</span>
          </>
        )}
        {volumeControls && (
          <div className={styles.volumeControls}>
            {volumeIcon}
            <Slider
              aria-labelledby="volume-slider"
              className={classnames(...sliderClasses)}
              id={sliderID}
              value={sliderValue}
              onChange={this.onVolumeChange}
            />
          </div>
        )}
      </>
    );
  }

  rightComponent() {
    const {
      pip,
      showFullscreen,
      playerService,
      onStreamQualityChange,
      isDesktop,
      onFullScreen,
      onTheatreMode,
      videoAndChatWrapper,
    } = this.props;

    const {
      pip: onPip,
      showQualityOptions,
      activeStreamQualityOption,
      streamQualityOptions,
    } = playerService;


    return (<>
      {showQualityOptions && (
        <StreamQualityOptions
          activeOption={activeStreamQualityOption}
          isDesktop={isDesktop}
          options={streamQualityOptions}
          videoAndChatWrapper={videoAndChatWrapper}
          onStreamQualityChange={onStreamQualityChange}
        />
      )}
      <TheatreMode videoAndChatWrapper={videoAndChatWrapper} onTheatreMode={onTheatreMode} />
      {showFullscreen && (
        <FullScreen className={styles.rightControlsItem} onFullScreen={onFullScreen} />
      )}
      {pip && (
        <PictureInPictureAlt className={styles.rightControlsItem} onClick={onPip} />
      )}
    </>);
  }

  onMuteVideo = () => {
    const { isJinkPlayer, jinkPlayer, playerService, handleVolume } = this.props;
    if (isJinkPlayer) {
      if (!jinkPlayer.muted()) {
        jinkPlayer.muted(true);
        handleVolume(0);
        SessionStorageService.set('profileVideoMuted', true);
      } else {
        jinkPlayer.muted(false);
        handleVolume();
        SessionStorageService.set('profileVideoMuted', false);
      }
    } else {
      playerService.toggleMuted();
    }
  };

  onPlay = () => {
    const { onPlay, playerService } = this.props;
    playerService.play();
    if (this.checkCallbacks(onPlay)) {
      onPlay();
    }
  };

  onPause = () => {
    const { onPause, playerService } = this.props;
    playerService.pause();
    if (this.checkCallbacks(onPause)) {
      onPause();
    }
  };

  onVolumeChange = (event, value) => {
    const {
      onVolumeChange,
      playerService,
      isJinkPlayer,
      jinkPlayer,
      handleVolume,
    } = this.props;

    if (isJinkPlayer) {
      handleVolume(value / 100);
      jinkPlayer.muted(!value);
    } else {
      playerService.setVolume(value);
      if (this.checkCallbacks(onVolumeChange)) {
        onVolumeChange(value);
      }
    }
  };

  render() {
    const {
      showSeekBar,
      playerService,
    } = this.props;

    const {
      seek: onSeek,
      seekValue,
      displayControls,
    } = playerService;

    const rightComponent = this.rightComponent();
    const leftComponent = this.leftComponent();

    const controlsClasses = classnames({
      [styles.opened]: displayControls,
      [styles.controls]: true,
    });

    return (
      <ControlsWrapper
        controlsClasses={controlsClasses}
        leftComponent={leftComponent}
        rightComponent={rightComponent}
        seekValue={seekValue}
        showSeekBar={showSeekBar}
        onSeekChange={onSeek}
      />
    );
  }
}

ViewerControls.defaultProps = {
  muted: false,
  isDesktop: true,
  // Toggle video seeking controls
  showSeekBar: true,
  // Toggle play/pause button
  showPlayPause: true,
  // Toggle video duration and current video time
  showTime: true,
  // Toggle volume controls
  volumeControls: true,
  // Shows prev & next buttons
  showPlaylistControls: true,
  pip: false,
  showFullscreen: true,
  volume: HALF_VOLUME,
  videoAndChatWrapper: {},
  handleVolume: () => undefined,
  onPlay: () => undefined,
  onPause: () => undefined,
  onFullScreen: () => undefined,
  onTheatreMode: () => undefined,
  onVolumeChange: () => undefined,
  onStreamQualityChange: () => undefined,
};

export default ViewerControls;
